/**
 * Vendor Profitability — Stage 12.
 *
 * The page sits under Vendors (`/vendors/profitability`) and answers a
 * single operator question: *which vendor's brands/products do we want
 * more of, scoped to a sub-category?* It composes:
 *
 *   - `useVendorProfitabilityCatalog` — quoted (catalog) margins, weighted
 *     by stock. Always available; no date window.
 *   - `useVendorProfitabilityRealized` — actualized margins from the
 *     selected window's sales. Same `(category > sub-cat > vendor > brand
 *     > product)` shape as Catalog, plus realized totals + benchmarks.
 *   - `useVendorProfitabilityQuadrant` — bubble plot for visual triage.
 *
 * The PivotTable + QuadrantChart sit side-by-side. Click a sub-cat row in
 * the table to focus its quadrant on the right.
 *
 * The "hierarchy" toggle re-pivots `vendor` and `brand` client-side from
 * the same payload — the leaf (product) rows already carry every weight
 * the helpers need.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Maximize2, Target } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { KPICard } from '@/components/charts/KPICard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  MultiCombobox,
  type MultiComboboxOption,
} from '@/components/ui/MultiCombobox';
import {
  PivotTable,
  type PivotColumn,
  type PivotNode,
} from '@/components/data/PivotTable';
import {
  QuadrantChart,
  type QuadrantChartPoint,
} from '@/components/charts/QuadrantChart';
import { VendorDecisionPicker } from '@/components/vendors/VendorDecisionPicker';
import {
  useInventoryTaxonomy,
  useVendorProfitabilityCatalog,
  useVendorProfitabilityQuadrant,
  useVendorProfitabilityRealized,
} from '@/api/bi/hooks';
import type {
  ProfitabilityLevel,
  ProfitabilityNode,
  VendorProfitabilityResponse,
} from '@/api/bi/types';
import { rangeISO, useFilters } from '@/state/filters';
import { useStoresList } from '@/api/legacy/stores';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

type HierarchyMode = 'vendor-first' | 'brand-first';
type Tab = 'catalog' | 'realized';
type ChartMode = 'vendor' | 'subcategory';

type ProfitabilityRow = ProfitabilityNode & PivotNode;

export function ProfitabilityPage() {
  const range = useFilters((s) => s.range);
  const globalStoreIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const [tab, setTab] = useState<Tab>('catalog');
  const [hierarchy, setHierarchy] = useState<HierarchyMode>('vendor-first');
  const [chartMode, setChartMode] = useState<ChartMode>('vendor');
  const [chartExpanded, setChartExpanded] = useState(false);

  // Filter rail state. Mirrors `SalesTab` inside `VendorDetailDialog` so an
  // operator coming from there has zero friction switching contexts.
  const [storeOverride, setStoreOverride] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);

  const taxonomy = useInventoryTaxonomy();
  const stores = useStoresList();

  // The "store override" wins. Otherwise inherit the global filter so the
  // page respects the topbar store picker when the user hasn't picked a
  // page-local override.
  const effectiveStoreIds = useMemo<number[] | null>(() => {
    if (storeOverride.length) return storeOverride;
    if (globalStoreIds.length) return globalStoreIds;
    return null;
  }, [storeOverride, globalStoreIds]);

  // Sub-categories cascade from the picked categories — same model as the
  // ledger TaxonomyRail.
  const visibleSubcats = useMemo(() => {
    if (!taxonomy.data) return [];
    if (categoryIds.length === 0) return taxonomy.data.subcategories;
    const allowed = new Set(categoryIds);
    return taxonomy.data.subcategories.filter((s) =>
      s.category_ids.some((cid) => allowed.has(cid)),
    );
  }, [taxonomy.data, categoryIds]);

  useEffect(() => {
    if (!taxonomy.data) return;
    const valid = new Set(visibleSubcats.map((s) => s.id));
    setSubcategoryIds((cur) => cur.filter((id) => valid.has(id)));
  }, [taxonomy.data, visibleSubcats]);

  const filterArgs = {
    storeIds: effectiveStoreIds,
    categoryIds: categoryIds.length ? categoryIds : null,
    subcategoryIds: subcategoryIds.length ? subcategoryIds : null,
    vendorIds: vendorIds.length ? vendorIds : null,
    brandIds: brandIds.length ? brandIds : null,
  };

  const catalog = useVendorProfitabilityCatalog({
    ...filterArgs,
    weightBasis: 'stock',
  });
  const realized = useVendorProfitabilityRealized({
    ...filterArgs,
    start: startISO,
    end: endISO,
    enabled: tab === 'realized',
  });
  const quadrant = useVendorProfitabilityQuadrant({
    ...filterArgs,
    start: startISO,
    end: endISO,
    top: 8,
    enabled: tab === 'realized',
  });

  // Pick the response that powers the active tab so the table, KPIs, and
  // chart all stay in sync.
  const active: VendorProfitabilityResponse | undefined =
    tab === 'realized' ? realized.data : catalog.data;
  const activeIsLoading = tab === 'realized' ? realized.isLoading : catalog.isLoading;
  const activeIsError = tab === 'realized' ? realized.isError : catalog.isError;
  const activeRefetch = tab === 'realized' ? realized.refetch : catalog.refetch;

  const reshapedRows = useMemo<ProfitabilityRow[]>(() => {
    if (!active?.rows?.length) return [];
    if (hierarchy === 'vendor-first') return active.rows as ProfitabilityRow[];
    return rebuildTree(active.rows, ['category', 'subcategory', 'brand', 'vendor', 'product']);
  }, [active, hierarchy]);

  // Anchor the chart to whichever sub-cat the user clicked (or focused
  // through the taxonomy filter). Defaults to the largest sub-cat.
  const [focusedSubcatId, setFocusedSubcatId] = useState<number | null>(null);
  useEffect(() => {
    if (focusedSubcatId != null) return;
    const first = quadrant.data?.subcategories?.[0]?.id ?? null;
    if (first != null) setFocusedSubcatId(first);
  }, [quadrant.data, focusedSubcatId]);
  useEffect(() => {
    if (subcategoryIds.length === 1) setFocusedSubcatId(subcategoryIds[0]);
  }, [subcategoryIds]);

  const focusedQuadrant = useMemo(
    () =>
      quadrant.data?.subcategories?.find((s) => s.id === focusedSubcatId) ??
      quadrant.data?.subcategories?.[0] ??
      null,
    [quadrant.data, focusedSubcatId],
  );

  const subcategoryQuadrant = useMemo(
    () => buildSubcategoryQuadrant(realized.data),
    [realized.data],
  );

  const kpis = useMemo(() => buildKPIs(active), [active]);

  const columns = useMemo<PivotColumn<ProfitabilityRow>[]>(
    () => buildColumns(tab),
    [tab],
  );

  const filterCount =
    storeOverride.length
    + categoryIds.length
    + subcategoryIds.length
    + vendorIds.length
    + brandIds.length;

  const renderQuadrantContent = (height?: number | string) => {
    if (tab === 'catalog') {
      return (
        <div className="px-5 py-10 text-center text-xs text-muted-foreground">
          The quadrant uses realized share and margin, so it only makes sense on
          the Realized tab.
        </div>
      );
    }

    if (chartMode === 'vendor' && quadrant.isLoading) {
      return (
        <div className="px-5 py-10 text-center text-xs text-muted-foreground">
          Loading vendor quadrant…
        </div>
      );
    }

    if (chartMode === 'subcategory' && realized.isLoading) {
      return (
        <div className="px-5 py-10 text-center text-xs text-muted-foreground">
          Loading sub-category quadrant…
        </div>
      );
    }

    if (chartMode === 'vendor' && quadrant.isError) {
      return (
        <ErrorState
          description="Couldn't load the quadrant data."
          onRetry={() => void quadrant.refetch()}
        />
      );
    }

    if (chartMode === 'subcategory' && realized.isError) {
      return (
        <ErrorState
          description="Couldn't load the sub-category quadrant data."
          onRetry={() => void realized.refetch()}
        />
      );
    }

    if (chartMode === 'vendor' && !focusedQuadrant) {
      return (
        <div className="px-5 py-10 text-center text-xs text-muted-foreground">
          No vendor activity for this filter.
        </div>
      );
    }

    if (chartMode === 'subcategory' && subcategoryQuadrant.points.length === 0) {
      return (
        <div className="px-5 py-10 text-center text-xs text-muted-foreground">
          No sub-category activity for this filter.
        </div>
      );
    }

    if (chartMode === 'vendor' && focusedQuadrant) {
      return (
        <>
          <QuadrantChart
            points={focusedQuadrant.points}
            formatNpr={(n) => fmt.npr(n)}
            height={height}
          />
          <div className="mt-2 px-5 text-[10px] text-muted-foreground">
            Sub-cat median margin:{' '}
            <span className="kpi-num text-foreground">
              {fmt.pct(focusedQuadrant.median_margin_pct)}
            </span>{' '}
            · Gross profit total:{' '}
            <span className="kpi-num text-foreground">
              {fmt.npr(focusedQuadrant.gross_profit_total)}
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <QuadrantChart
          points={subcategoryQuadrant.points}
          formatNpr={(n) => fmt.npr(n)}
          xLabel="Revenue share"
          height={height}
          onPointClick={(point) => {
            if (typeof point.id === 'number') setFocusedSubcatId(point.id);
          }}
        />
        <div className="mt-2 px-5 text-[10px] text-muted-foreground">
          Median sub-cat margin:{' '}
          <span className="kpi-num text-foreground">
            {fmt.pct(subcategoryQuadrant.medianMarginPct)}
          </span>{' '}
          · Revenue total:{' '}
          <span className="kpi-num text-foreground">
            {fmt.npr(subcategoryQuadrant.revenueTotal)}
          </span>{' '}
          · Gross profit:{' '}
          <span className="kpi-num text-foreground">
            {fmt.npr(subcategoryQuadrant.grossProfitTotal)}
          </span>
        </div>
      </>
    );
  };

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Vendor profitability"
        description="Who to push, who to wind down — vendor-vs-vendor margin within each sub-category. Pick a row to focus the quadrant chart on the right."
        actions={
          <HierarchyToggle value={hierarchy} onChange={setHierarchy} />
        }
      />

      <FilterRail
        stores={stores.data ?? []}
        storesLoading={stores.isLoading}
        storeOverride={storeOverride}
        setStoreOverride={setStoreOverride}
        globalStoreCount={globalStoreIds.length}
        categoryIds={categoryIds}
        setCategoryIds={setCategoryIds}
        subcategoryIds={subcategoryIds}
        setSubcategoryIds={setSubcategoryIds}
        vendorIds={vendorIds}
        setVendorIds={setVendorIds}
        brandIds={brandIds}
        setBrandIds={setBrandIds}
        taxonomyLoading={taxonomy.isLoading}
        categories={taxonomy.data?.categories ?? []}
        subcategories={visibleSubcats}
        vendors={taxonomy.data?.vendors ?? []}
        brands={taxonomy.data?.brands ?? []}
        filterCount={filterCount}
        onClear={() => {
          setStoreOverride([]);
          setCategoryIds([]);
          setSubcategoryIds([]);
          setVendorIds([]);
          setBrandIds([]);
        }}
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Vendors covered"
          value={fmt.number(kpis.vendorCount)}
          loading={activeIsLoading}
        />
        <KPICard
          label="Sub-cats analysed"
          value={fmt.number(kpis.subcategoryCount)}
          loading={activeIsLoading}
        />
        <KPICard
          label={tab === 'realized' ? 'Weighted gross margin' : 'Quoted margin'}
          value={fmt.pct(kpis.weightedMarginPct)}
          loading={activeIsLoading}
          hint={
            tab === 'realized'
              ? 'Σ(SP·units) − Σ(CP·units) over Σ(SP·units)'
              : 'Σ(SP·stock) − Σ(CP·stock) over Σ(SP·stock)'
          }
        />
        <KPICard
          label={tab === 'realized' ? 'Gross profit' : 'SKUs in scope'}
          value={
            tab === 'realized'
              ? fmt.npr(kpis.grossProfit ?? 0)
              : fmt.number(kpis.skuCount)
          }
          loading={activeIsLoading}
        />
      </div>

      <div className="mt-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="catalog">Catalog</TabsTrigger>
            <TabsTrigger value="realized">
              Realized
              <span className="ml-1 hidden text-[10px] text-muted-foreground sm:inline">
                · {fmt.date(range.from)} → {fmt.date(range.to)}
              </span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <TabsContent value="catalog" className="mt-0">
              <PivotPanel
                rows={reshapedRows}
                columns={columns}
                isLoading={activeIsLoading}
                isError={activeIsError}
                onRetry={() => void activeRefetch()}
                onSelect={(row) => {
                  if (row.level === 'subcategory' && row.ids.subcategory_id) {
                    setFocusedSubcatId(row.ids.subcategory_id);
                  }
                }}
                selectedId={selectedSubcatRowId(reshapedRows, focusedSubcatId)}
                title="Catalog margin pivot"
                description="Quoted vendor margins from the active VendorProduct catalog. Stock-weighted at every parent row."
                csvName="vendor-profitability-catalog.csv"
              />
            </TabsContent>

            <TabsContent value="realized" className="mt-0">
              <PivotPanel
                rows={reshapedRows}
                columns={columns}
                isLoading={activeIsLoading}
                isError={activeIsError}
                onRetry={() => void activeRefetch()}
                onSelect={(row) => {
                  if (row.level === 'subcategory' && row.ids.subcategory_id) {
                    setFocusedSubcatId(row.ids.subcategory_id);
                  }
                }}
                selectedId={selectedSubcatRowId(reshapedRows, focusedSubcatId)}
                title="Realized margin pivot"
                description={`Margins actualised from sales between ${fmt.date(range.from)} and ${fmt.date(range.to)}. Units-weighted at every parent row.`}
                csvName="vendor-profitability-realized.csv"
              />
            </TabsContent>

            <Card className="lg:sticky lg:top-4 lg:self-start">
              <CardHeader>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle>
                      {chartMode === 'vendor' ? 'Vendor quadrant' : 'Sub-category quadrant'}
                    </CardTitle>
                    <ChartModeToggle value={chartMode} onChange={setChartMode} />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {tab === 'realized'
                      ? chartMode === 'vendor'
                        ? focusedQuadrant?.name ?? 'Pick a sub-category from the table to focus the chart.'
                        : 'Compare sub-categories by revenue share & margin Δ.'
                      : 'Switch to Realized to compare profitability by share & margin Δ.'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="press rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-ring disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Expand chart"
                    title="Expand chart"
                    disabled={tab !== 'realized'}
                    onClick={() => setChartExpanded(true)}
                  >
                    <Maximize2 className="h-4 w-4" aria-hidden />
                  </button>
                  <Target className="h-4 w-4 text-muted-foreground" aria-hidden />
                </div>
              </CardHeader>
              <CardBody className="px-0 pb-3 pt-0">
                {renderQuadrantContent()}
              </CardBody>
            </Card>
          </div>
        </Tabs>
      </div>

      <Dialog open={chartExpanded} onOpenChange={setChartExpanded}>
        <DialogContent className="h-screen max-h-screen max-w-none rounded-none border-0 p-0">
          <div className="flex h-full flex-col bg-surface-elevated">
            <div className="border-b border-border px-5 py-4 pr-14">
              <DialogTitle className="text-base font-semibold text-foreground">
                {chartMode === 'vendor' ? 'Vendor quadrant' : 'Sub-category quadrant'}
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs text-muted-foreground">
                {chartMode === 'vendor'
                  ? focusedQuadrant?.name ?? 'Vendor profitability quadrant'
                  : 'Compare sub-categories by revenue share & margin Δ.'}
              </DialogDescription>
            </div>
            <div className="min-h-0 flex-1 px-5 py-4">
              {renderQuadrantContent('calc(100vh - 140px)')}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// KPI helpers
// ---------------------------------------------------------------------------

interface ProfitabilityKpis {
  vendorCount: number;
  subcategoryCount: number;
  weightedMarginPct: number;
  grossProfit: number | null;
  skuCount: number;
}

interface SubcategoryQuadrantData {
  points: QuadrantChartPoint[];
  medianMarginPct: number;
  grossProfitTotal: number;
  revenueTotal: number;
}

function buildKPIs(resp: VendorProfitabilityResponse | undefined): ProfitabilityKpis {
  if (!resp) {
    return {
      vendorCount: 0,
      subcategoryCount: 0,
      weightedMarginPct: 0,
      grossProfit: null,
      skuCount: 0,
    };
  }
  const subcategoryCount = resp.subcategory_summary.length;
  const vendorIds = new Set<number>();
  let cpSum = 0;
  let spSum = 0;
  let gpSum = 0;
  let realizedRevenueSum = 0;
  let hasRealizedRows = false;
  let skuCount = 0;
  for (const r of resp.rows) {
    if (r.level === 'vendor' && r.ids.vendor_id != null) {
      vendorIds.add(r.ids.vendor_id);
    }
    if (r.level === 'product') {
      const w = r.metrics.weight || 0;
      cpSum += sellableCpPerUnit(r) * w;
      spSum += r.metrics.sp_per_unit * w;
      skuCount += r.metrics.sku_count || 0;
      if (r.realized) {
        hasRealizedRows = true;
        gpSum += realizedGrossProfit(r);
        realizedRevenueSum += r.realized.revenue ?? 0;
      }
    }
  }
  const weightedMarginPct = hasRealizedRows
    ? (realizedRevenueSum > 0 ? gpSum / realizedRevenueSum : 0)
    : (spSum > 0 ? (spSum - cpSum) / spSum : 0);
  return {
    vendorCount: vendorIds.size,
    subcategoryCount,
    weightedMarginPct,
    grossProfit: gpSum,
    skuCount,
  };
}

function buildSubcategoryQuadrant(
  resp: VendorProfitabilityResponse | undefined,
): SubcategoryQuadrantData {
  const subcategoryRows = (resp?.rows ?? []).filter(
    (row) => row.level === 'subcategory' && row.realized,
  );
  const revenueTotal = subcategoryRows.reduce(
    (sum, row) => sum + (row.realized?.revenue ?? 0),
    0,
  );
  const grossProfitTotal = subcategoryRows.reduce(
    (sum, row) => sum + realizedGrossProfit(row),
    0,
  );
  const margins = subcategoryRows.map(realizedGrossMarginPct);
  const medianMarginPct = median(margins);

  return {
    revenueTotal,
    grossProfitTotal,
    medianMarginPct,
    points: subcategoryRows.map((row) => {
      const revenue = row.realized?.revenue ?? 0;
      return {
        id: row.ids.subcategory_id ?? row.id,
        label: row.label,
        x: revenueTotal > 0 ? revenue / revenueTotal : 0,
        y: realizedGrossMarginPct(row) - medianMarginPct,
        z: realizedGrossProfit(row),
        decision: null,
        composite_score: null,
      };
    }),
  };
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

// ---------------------------------------------------------------------------
// Column factory
// ---------------------------------------------------------------------------

function buildColumns(tab: Tab): PivotColumn<ProfitabilityRow>[] {
  const cp: PivotColumn<ProfitabilityRow> = {
    id: 'cp',
    header: 'Cost',
    helpText: 'Weighted average sellable SKU cost. Raw base cost × bundle units.',
    align: 'right',
    render: (r) => <span className="kpi-num">{fmt.npr(sellableCpPerUnit(r))}</span>,
    toCsv: (r) => String(sellableCpPerUnit(r)),
  };
  const sp: PivotColumn<ProfitabilityRow> = {
    id: 'sp',
    header: 'Selling',
    helpText: 'Weighted average selling price per unit. Weight = stock or units sold.',
    align: 'right',
    render: (r) => <span className="kpi-num">{fmt.npr(r.metrics.sp_per_unit)}</span>,
    toCsv: (r) => String(r.metrics.sp_per_unit ?? ''),
  };
  const margin: PivotColumn<ProfitabilityRow> = {
    id: 'margin',
    header: 'Margin %',
    helpText:
      tab === 'realized'
        ? 'Actual gross profit over actual revenue for the selected sales window.'
        : 'Σ(SP·w) − Σ(CP·w) over Σ(SP·w). Parent rows are weighted, never simple averages.',
    align: 'right',
    render: (r) => (
      <MarginCell value={tab === 'realized' ? realizedGrossMarginPct(r) : r.metrics.margin_pct} />
    ),
    toCsv: (r) => String(tab === 'realized' ? realizedGrossMarginPct(r) : r.metrics.margin_pct ?? ''),
  };
  const markup: PivotColumn<ProfitabilityRow> = {
    id: 'markup',
    header: 'Markup %',
    helpText: '(SP − CP) over CP — the same trade as margin but framed as a multiplier.',
    align: 'right',
    render: (r) => (
      <span className="kpi-num text-muted-foreground">{fmt.pct(r.metrics.markup_pct)}</span>
    ),
    toCsv: (r) => String(r.metrics.markup_pct ?? ''),
  };
  const offMrp: PivotColumn<ProfitabilityRow> = {
    id: 'mrp',
    header: 'Off MRP',
    helpText: '(MRP − SP) over MRP. How much we discount off the printed sticker.',
    align: 'right',
    render: (r) => (
      <span className="kpi-num text-muted-foreground">
        {fmt.pct(r.metrics.discount_off_mrp_pct)}
      </span>
    ),
    toCsv: (r) => String(r.metrics.discount_off_mrp_pct ?? ''),
  };
  const skus: PivotColumn<ProfitabilityRow> = {
    id: 'skus',
    header: 'SKUs',
    helpText: 'Number of distinct products under this row.',
    align: 'right',
    render: (r) => <span className="kpi-num text-xs">{fmt.number(r.metrics.sku_count)}</span>,
    toCsv: (r) => String(r.metrics.sku_count ?? ''),
  };

  if (tab === 'catalog') {
    return [cp, sp, margin, markup, offMrp, skus];
  }

  // Realized adds units, gross profit, sales/margin share.
  const units: PivotColumn<ProfitabilityRow> = {
    id: 'units',
    header: 'Units',
    helpText: 'Units sold inside the active date window.',
    align: 'right',
    render: (r) => (
      <span className="kpi-num">{fmt.number(r.realized?.units ?? 0)}</span>
    ),
    toCsv: (r) => String(r.realized?.units ?? ''),
  };
  const revenue: PivotColumn<ProfitabilityRow> = {
    id: 'revenue',
    header: 'Revenue',
    helpText: 'Σ(unit_price · qty) net of returns at OrderItem level.',
    align: 'right',
    render: (r) => (
      <span className="kpi-num">{fmt.npr(r.realized?.revenue ?? 0)}</span>
    ),
    toCsv: (r) => String(r.realized?.revenue ?? ''),
  };
  const gp: PivotColumn<ProfitabilityRow> = {
    id: 'gp',
    header: 'Gross profit',
    helpText: 'Revenue − BI cost basis at the leaf product level, then summed up.',
    align: 'right',
    render: (r) => (
      <span className="kpi-num font-medium">{fmt.npr(realizedGrossProfit(r))}</span>
    ),
    toCsv: (r) => (r.realized ? String(realizedGrossProfit(r)) : ''),
  };
  const share: PivotColumn<ProfitabilityRow> = {
    id: 'share',
    header: 'Sales share',
    helpText: "Vendor's share of sub-category revenue. Sums to 100 % across vendors.",
    align: 'right',
    render: (r) =>
      r.benchmark ? (
        <span className="kpi-num text-xs">{fmt.pct(r.benchmark.sales_share)}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    toCsv: (r) => String(r.benchmark?.sales_share ?? ''),
  };
  const delta: PivotColumn<ProfitabilityRow> = {
    id: 'delta',
    header: 'Δ vs median',
    helpText:
      "Vendor's margin minus the sub-category median margin, in percentage points.",
    align: 'right',
    render: (r) =>
      r.benchmark ? <DeltaCell value={r.benchmark.margin_delta_vs_subcat_median} /> : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    toCsv: (r) => String(r.benchmark?.margin_delta_vs_subcat_median ?? ''),
  };

  return [units, revenue, gp, margin, share, delta, skus];
}

function MarginCell({ value }: { value: number }) {
  const tone = value < 0 ? 'text-danger' : value < 0.05 ? 'text-warning-soft' : 'text-foreground';
  return <span className={cn('kpi-num font-medium', tone)}>{fmt.pct(value)}</span>;
}

function realizedGrossMarginPct(row: ProfitabilityNode): number {
  const revenue = row.realized?.revenue ?? 0;
  if (revenue === 0) return 0;
  return realizedGrossProfit(row) / revenue;
}

function realizedCogs(row: ProfitabilityNode): number {
  const units = row.realized?.units ?? 0;
  return sellableCpPerUnit(row) * units;
}

function realizedGrossProfit(row: ProfitabilityNode): number {
  const revenue = row.realized?.revenue ?? 0;
  return revenue - realizedCogs(row);
}

function baseCpPerUnit(row: ProfitabilityNode): number {
  return row.metrics.base_cp_per_unit ?? row.metrics.cp_per_unit ?? 0;
}

function bundleUnits(row: ProfitabilityNode): number {
  return row.metrics.bundle_units ?? 1;
}

function sellableCpPerUnit(row: ProfitabilityNode): number {
  return row.metrics.sellable_cp_per_unit ?? baseCpPerUnit(row) * bundleUnits(row);
}

function DeltaCell({ value }: { value: number }) {
  const tone = value > 0 ? 'text-success' : value < 0 ? 'text-danger' : 'text-muted-foreground';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={cn('kpi-num text-xs font-medium', tone)}>
      {sign}
      {(value * 100).toFixed(1)} pp
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pivot panel — wraps PivotTable and injects the per-row decision picker.
// ---------------------------------------------------------------------------

interface PivotPanelProps {
  rows: ProfitabilityRow[];
  columns: PivotColumn<ProfitabilityRow>[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (row: ProfitabilityRow) => void;
  selectedId: string | null;
  title: string;
  description: string;
  csvName: string;
}

function PivotPanel({
  rows,
  columns,
  isLoading,
  isError,
  onRetry,
  onSelect,
  selectedId,
  title,
  description,
  csvName,
}: PivotPanelProps) {
  if (isError) {
    return (
      <ErrorState
        description="Couldn't load the profitability pivot."
        onRetry={onRetry}
      />
    );
  }

  return (
    <PivotTable<ProfitabilityRow>
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      onRowSelect={onSelect}
      selectedId={selectedId}
      title={title}
      description={description}
      csvFileName={csvName}
      defaultExpandLevels={['category']}
      density="compact"
      rowAccessory={(row) =>
        row.level === 'vendor' && row.ids.vendor_id && row.ids.subcategory_id ? (
          <VendorDecisionPicker
            vendorId={row.ids.vendor_id}
            subcategoryId={row.ids.subcategory_id}
            current={row.decision ?? null}
            compact
          />
        ) : null
      }
    />
  );
}

function selectedSubcatRowId(rows: ProfitabilityRow[], subcatId: number | null): string | null {
  if (subcatId == null) return null;
  const row = rows.find(
    (r) => r.level === 'subcategory' && r.ids.subcategory_id === subcatId,
  );
  return row?.id ?? null;
}

// ---------------------------------------------------------------------------
// Hierarchy toggle + filter rail
// ---------------------------------------------------------------------------

function ChartModeToggle({
  value,
  onChange,
}: {
  value: ChartMode;
  onChange: (next: ChartMode) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Profitability quadrant mode"
      className="inline-flex overflow-hidden rounded-md border border-border bg-surface text-xs"
    >
      {([
        { v: 'vendor', label: 'Vendor' },
        { v: 'subcategory', label: 'Sub-category' },
      ] as const).map((opt, i) => {
        const active = value === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.v)}
            className={cn(
              'h-7 border-r border-border px-2 font-medium transition-colors',
              i === 1 && 'border-r-0',
              active
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted/40',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function HierarchyToggle({
  value,
  onChange,
}: {
  value: HierarchyMode;
  onChange: (next: HierarchyMode) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border bg-surface text-xs">
      {([
        { v: 'vendor-first', label: 'Vendor → Brand' },
        { v: 'brand-first', label: 'Brand → Vendor' },
      ] as const).map((opt, i) => {
        const active = value === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            className={cn(
              'h-7 border-r border-border px-2 font-medium transition-colors',
              i === 1 && 'border-r-0',
              active
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted/40',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

interface FilterRailProps {
  stores: { id: number; name: string; is_online?: boolean }[];
  storesLoading: boolean;
  storeOverride: number[];
  setStoreOverride: (ids: number[]) => void;
  globalStoreCount: number;
  categoryIds: number[];
  setCategoryIds: (ids: number[]) => void;
  subcategoryIds: number[];
  setSubcategoryIds: (ids: number[]) => void;
  vendorIds: number[];
  setVendorIds: (ids: number[]) => void;
  brandIds: number[];
  setBrandIds: (ids: number[]) => void;
  taxonomyLoading: boolean;
  categories: { id: number; name: string }[];
  subcategories: { id: number; name: string }[];
  vendors: { id: number; name: string }[];
  brands: { id: number; name: string }[];
  filterCount: number;
  onClear: () => void;
}

function FilterRail(props: FilterRailProps) {
  const storeOpts: MultiComboboxOption[] = useMemo(
    () => props.stores.map((s) => ({ value: String(s.id), label: s.name })),
    [props.stores],
  );
  const categoryOpts: MultiComboboxOption[] = useMemo(
    () => props.categories.map((c) => ({ value: String(c.id), label: c.name })),
    [props.categories],
  );
  const subcategoryOpts: MultiComboboxOption[] = useMemo(
    () => props.subcategories.map((s) => ({ value: String(s.id), label: s.name })),
    [props.subcategories],
  );
  const vendorOpts: MultiComboboxOption[] = useMemo(
    () => props.vendors.map((v) => ({ value: String(v.id), label: v.name })),
    [props.vendors],
  );
  const brandOpts: MultiComboboxOption[] = useMemo(
    () => props.brands.map((b) => ({ value: String(b.id), label: b.name })),
    [props.brands],
  );

  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <FilterLabel>Store</FilterLabel>
        <MultiCombobox
          values={props.storeOverride.map(String)}
          onChange={(v) => props.setStoreOverride(v.map(Number))}
          options={storeOpts}
          placeholder={
            props.globalStoreCount
              ? `Inherits ${props.globalStoreCount} store filter`
              : 'All stores'
          }
          searchPlaceholder="Search store…"
          allLabel={
            props.globalStoreCount ? 'Inherit global filter' : 'All stores'
          }
          countNoun="store"
          loading={props.storesLoading}
          triggerClassName="h-8 text-xs min-w-[180px]"
          width={240}
        />

        <FilterLabel>Category</FilterLabel>
        <MultiCombobox
          values={props.categoryIds.map(String)}
          onChange={(v) => props.setCategoryIds(v.map(Number))}
          options={categoryOpts}
          placeholder="All categories"
          searchPlaceholder="Search category…"
          allLabel="All categories"
          countNoun="category"
          loading={props.taxonomyLoading}
          triggerClassName="h-8 text-xs min-w-[160px]"
          width={240}
        />

        <FilterLabel>Sub-category</FilterLabel>
        <MultiCombobox
          values={props.subcategoryIds.map(String)}
          onChange={(v) => props.setSubcategoryIds(v.map(Number))}
          options={subcategoryOpts}
          placeholder="All sub-categories"
          searchPlaceholder="Search sub-category…"
          allLabel="All sub-categories"
          countNoun="sub-cat"
          loading={props.taxonomyLoading}
          triggerClassName="h-8 text-xs min-w-[160px]"
          width={240}
        />

        <FilterLabel>Vendor</FilterLabel>
        <MultiCombobox
          values={props.vendorIds.map(String)}
          onChange={(v) => props.setVendorIds(v.map(Number))}
          options={vendorOpts}
          placeholder="All vendors"
          searchPlaceholder="Search vendor…"
          allLabel="All vendors"
          countNoun="vendor"
          loading={props.taxonomyLoading}
          triggerClassName="h-8 text-xs min-w-[160px]"
          width={240}
        />

        <FilterLabel>Brand</FilterLabel>
        <MultiCombobox
          values={props.brandIds.map(String)}
          onChange={(v) => props.setBrandIds(v.map(Number))}
          options={brandOpts}
          placeholder="All brands"
          searchPlaceholder="Search brand…"
          allLabel="All brands"
          countNoun="brand"
          loading={props.taxonomyLoading}
          triggerClassName="h-8 text-xs min-w-[160px]"
          width={240}
        />

        {props.filterCount > 0 ? (
          <button
            type="button"
            className="ml-auto inline-flex h-8 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            onClick={props.onClear}
          >
            Clear ({props.filterCount})
          </button>
        ) : null}
      </div>
    </div>
  );
}

function FilterLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Client-side tree rebuild — used only when the operator switches to the
// "Brand → Vendor" view. Walks every leaf product row and re-aggregates
// upwards using the same weighting the backend helper uses, so margin
// percentages stay honest after the re-pivot.
// ---------------------------------------------------------------------------

const HIERARCHY_KEY: Record<ProfitabilityLevel, keyof ProfitabilityNode['ids']> = {
  category: 'category_id',
  subcategory: 'subcategory_id',
  vendor: 'vendor_id',
  brand: 'brand_id',
  product: 'product_id',
};

function rebuildTree(
  rows: ProfitabilityNode[],
  order: ProfitabilityLevel[],
): ProfitabilityRow[] {
  const productLeaves = rows.filter((r) => r.level === 'product');
  if (!productLeaves.length) return [];

  // Index categories / sub-cats / vendors / brands so the rebuild can
  // re-use the original labels (and decisions) instead of looking them
  // up from a separate API.
  const labelByLevelId = new Map<string, string>();
  const decisionByVendorSub = new Map<string, ProfitabilityNode['decision']>();
  const subcatSecondaryByid = new Map<number, number[]>();
  for (const r of rows) {
    const id = r.ids[HIERARCHY_KEY[r.level]];
    if (id != null) labelByLevelId.set(`${r.level}:${id}`, r.label);
    if (r.level === 'subcategory' && r.ids.subcategory_id != null) {
      subcatSecondaryByid.set(r.ids.subcategory_id, r.secondary_category_ids);
    }
    if (r.level === 'vendor' && r.decision && r.ids.subcategory_id != null && r.ids.vendor_id != null) {
      decisionByVendorSub.set(`${r.ids.subcategory_id}:${r.ids.vendor_id}`, r.decision);
    }
  }

  // Group leaves by composite key built from the requested hierarchy.
  interface Bucket {
    level: ProfitabilityLevel;
    label: string;
    ids: ProfitabilityNode['ids'];
    keyPath: string[];
    parentKey: string | null;
    cpSum: number;
    baseCpSum: number;
    bundleUnitsSum: number;
    spSum: number;
    mrpSum: number;
    weightSum: number;
    skuCount: number;
    skuSold: number;
    realizedUnits: number;
    realizedRevenue: number;
    realizedCogs: number;
    realizedGp: number;
    children: Set<string>;
    leaf: ProfitabilityNode | null;
  }

  const buckets = new Map<string, Bucket>();

  const ensureBucket = (
    level: ProfitabilityLevel,
    keyPath: string[],
    label: string,
    ids: ProfitabilityNode['ids'],
    parentKey: string | null,
  ): Bucket => {
    const key = keyPath.join('/');
    let b = buckets.get(key);
    if (!b) {
      b = {
        level,
        label,
        ids,
        keyPath,
        parentKey,
        cpSum: 0,
        baseCpSum: 0,
        bundleUnitsSum: 0,
        spSum: 0,
        mrpSum: 0,
        weightSum: 0,
        skuCount: 0,
        skuSold: 0,
        realizedUnits: 0,
        realizedRevenue: 0,
        realizedCogs: 0,
        realizedGp: 0,
        children: new Set(),
        leaf: null,
      };
      buckets.set(key, b);
    }
    return b;
  };

  for (const leaf of productLeaves) {
    const trail: { level: ProfitabilityLevel; key: string; label: string; ids: ProfitabilityNode['ids'] }[] = [];
    const idsAt: Record<string, number> = {};
    for (const lvl of order) {
      const idKey = HIERARCHY_KEY[lvl];
      const id = leaf.ids[idKey];
      if (id == null) {
        // Leaf doesn't belong under this re-pivot (e.g. unbranded product
        // when "brand-first" is selected). Drop it from the rebuilt tree.
        trail.length = 0;
        break;
      }
      idsAt[idKey] = id;
      const segLabel = labelByLevelId.get(`${lvl}:${id}`) ?? `${lvl} ${id}`;
      trail.push({
        level: lvl,
        key: `${lvl}:${id}`,
        label: segLabel,
        ids: { ...leaf.ids, ...idsAt } as ProfitabilityNode['ids'],
      });
    }
    if (!trail.length) continue;

    let parentKey: string | null = null;
    const keyPath: string[] = [];
    for (const seg of trail) {
      keyPath.push(seg.key);
      const bucket = ensureBucket(seg.level, [...keyPath], seg.label, seg.ids, parentKey);

      const w = leaf.metrics.weight || 0;
      bucket.cpSum += sellableCpPerUnit(leaf) * w;
      bucket.baseCpSum += baseCpPerUnit(leaf) * w;
      bucket.bundleUnitsSum += bundleUnits(leaf) * w;
      bucket.spSum += leaf.metrics.sp_per_unit * w;
      bucket.mrpSum += leaf.metrics.mrp_per_unit * w;
      bucket.weightSum += w;
      if (seg.level !== 'product') {
        bucket.skuCount += leaf.metrics.sku_count || 0;
      }
      if (leaf.metrics.sku_count_sold) bucket.skuSold += leaf.metrics.sku_count_sold;
      if (leaf.realized) {
        bucket.realizedUnits += leaf.realized.units || 0;
        bucket.realizedRevenue += leaf.realized.revenue || 0;
        bucket.realizedCogs += realizedCogs(leaf);
        bucket.realizedGp += realizedGrossProfit(leaf);
      }
      if (parentKey) buckets.get(parentKey)?.children.add(bucket.keyPath.join('/'));
      parentKey = bucket.keyPath.join('/');
      if (seg.level === 'product') bucket.leaf = leaf;
    }
  }

  // Walk depth-first so the flat output is still in tree order.
  const out: ProfitabilityRow[] = [];
  const visit = (key: string, depth: number) => {
    const b = buckets.get(key);
    if (!b) return;
    out.push(toRow(b, decisionByVendorSub, subcatSecondaryByid));
    const childKeys = Array.from(b.children).sort((a, b2) => {
      // Stable sort so the same input always emits the same output.
      const la = buckets.get(a)?.label ?? '';
      const lb = buckets.get(b2)?.label ?? '';
      return la.localeCompare(lb);
    });
    for (const ck of childKeys) visit(ck, depth + 1);
  };
  const roots = Array.from(buckets.values()).filter((b) => b.parentKey === null);
  for (const r of roots.sort((a, b) => a.label.localeCompare(b.label))) {
    visit(r.keyPath.join('/'), 0);
  }
  return out;
}

function toRow(
  b: {
    level: ProfitabilityLevel;
    label: string;
    ids: ProfitabilityNode['ids'];
    keyPath: string[];
    parentKey: string | null;
    cpSum: number;
    baseCpSum: number;
    bundleUnitsSum: number;
    spSum: number;
    mrpSum: number;
    weightSum: number;
    skuCount: number;
    skuSold: number;
    realizedUnits: number;
    realizedRevenue: number;
    realizedCogs: number;
    realizedGp: number;
    leaf: ProfitabilityNode | null;
  },
  decisions: Map<string, ProfitabilityNode['decision']>,
  subcatSecondary: Map<number, number[]>,
): ProfitabilityRow {
  // Use the leaf's per-unit numbers when this is itself a product row; for
  // every parent we report weighted averages from the running sums.
  const w = b.weightSum;
  const cpPerUnit = w > 0 ? b.baseCpSum / w : b.leaf ? baseCpPerUnit(b.leaf) : 0;
  const bundleUnitsValue = w > 0 ? b.bundleUnitsSum / w : b.leaf ? bundleUnits(b.leaf) : 1;
  const sellableCp = w > 0 ? b.cpSum / w : b.leaf ? sellableCpPerUnit(b.leaf) : 0;
  const spPerUnit = w > 0 ? b.spSum / w : b.leaf?.metrics.sp_per_unit ?? 0;
  const mrpPerUnit = w > 0 ? b.mrpSum / w : b.leaf?.metrics.mrp_per_unit ?? 0;
  const marginPct = b.spSum > 0 ? (b.spSum - b.cpSum) / b.spSum : 0;
  const markupPct = b.cpSum > 0 ? (b.spSum - b.cpSum) / b.cpSum : 0;
  const offMrpPct = b.mrpSum > 0 ? (b.mrpSum - b.spSum) / b.mrpSum : 0;

  const decisionKey =
    b.level === 'vendor' && b.ids.subcategory_id != null && b.ids.vendor_id != null
      ? `${b.ids.subcategory_id}:${b.ids.vendor_id}`
      : null;
  const decision = decisionKey ? decisions.get(decisionKey) : undefined;

  const realized: ProfitabilityNode['realized'] | undefined = b.realizedUnits || b.realizedRevenue
    ? {
        units: b.realizedUnits,
        revenue: b.realizedRevenue,
        cogs: b.realizedCogs,
        gross_profit: b.realizedGp,
        promo_cost_vendor: 0,
        promo_cost_fasto: 0,
        returns_value: 0,
        damages_value: 0,
        net_margin_pct: b.realizedRevenue > 0 ? b.realizedGp / b.realizedRevenue : 0,
      }
    : undefined;

  return {
    id: b.keyPath.join('/'),
    parent_id: b.parentKey,
    level: b.level,
    label: b.label,
    path: b.keyPath,
    metrics: {
      cp_per_unit: cpPerUnit,
      base_cp_per_unit: cpPerUnit,
      bundle_units: bundleUnitsValue,
      sellable_cp_per_unit: sellableCp,
      sp_per_unit: spPerUnit,
      mrp_per_unit: mrpPerUnit,
      margin_pct: marginPct,
      markup_pct: markupPct,
      discount_off_mrp_pct: offMrpPct,
      sku_count: b.level === 'product' ? 1 : b.skuCount,
      sku_count_sold: b.skuSold || null,
      weight: w,
    },
    realized,
    benchmark: undefined,
    ids: b.ids,
    warnings: marginPct < 0 ? ['negative_margin'] : [],
    secondary_category_ids:
      b.level === 'subcategory' && b.ids.subcategory_id != null
        ? subcatSecondary.get(b.ids.subcategory_id) ?? []
        : [],
    decision,
  };
}
